import type Owner from '@ember/owner';
import EmberService, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type {
	MetricInfoCardApi,
	MetricInfoCardOpenOptions,
} from '@databox/ember-integrations/lib/components/MetricInfoCard/MetricInfoCardEntryPoint';

import type { Service } from 'webapp-types';

import type { Metric, MetricFromClient } from 'webapp/models-custom';

export type MetricInfoTarget = Parameters<MetricInfoCardApi['toggle']>[1];
export type MetricInfoModel = Metric | MetricFromClient;
export type { MetricInfoCardOpenOptions };

// The editors' unsaved metric (`MetricFromClient` keyed `temporary`): it has no registry row to look up.
const TEMPORARY_KEY = 'temporary';

/**
 * The React metric info card (`components/metric-info/manager`): one card for the whole app, anchored to the Ember
 * element that opened it. Anchors reach it through the `metric-info-anchor` modifier; the card closes on every route
 * change and whenever an Ember popup opens. In the designer it is always dark, like the designer's own panels.
 */
export default class MetricInfoService extends EmberService {
	@service('navigation') declare navigationService: Service.Navigation;
	@service declare router: Service.Router;

	// Set once the manager's React root has mounted; until then the anchors do nothing.
	@tracked api: MetricInfoCardApi | null = null;

	constructor(owner: Owner) {
		super(owner);

		this.router.on('routeWillChange', () => this.close());
	}

	// Looked up by key and `spaceAccessId` (null for a catalogue or template metric); `preview`, what Ember knows of the
	// metric, shows if the lookup fails. An unsaved metric is preview only.
	toTarget(model: MetricInfoModel): MetricInfoTarget {
		const preview = {
			name: model.name,
			description: model.description || null,
			source: { name: model.sourceName, icon: model.sourceAvatar },
		};

		if (model.key === TEMPORARY_KEY) {
			return { preview };
		}

		return {
			metricSetup: { key: model.key, spaceAccessId: model.spaceAccessId ?? null },
			preview,
		};
	}

	// Read on each open, not set on the card's root: a route change doesn't re-render it.
	toOptions(options: MetricInfoCardOpenOptions): MetricInfoCardOpenOptions {
		return this.navigationService.isDesigner ? { ...options, theme: 'dark' } : options;
	}

	hoverEnter(
		anchor: HTMLElement,
		model: MetricInfoModel,
		options: MetricInfoCardOpenOptions,
	): void {
		this.api?.anchorEnter(anchor, this.toTarget(model), this.toOptions(options));
	}

	hoverLeave(): void {
		this.api?.anchorLeave();
	}

	toggle(anchor: HTMLElement, model: MetricInfoModel, options: MetricInfoCardOpenOptions): void {
		this.api?.toggle(anchor, this.toTarget(model), this.toOptions(options));
	}

	close(): void {
		this.api?.close();
	}

	closeIfAnchor(anchor: HTMLElement): void {
		this.api?.closeIfAnchor(anchor);
	}
}

declare module '@ember/service' {
	interface Registry {
		'metric-info': MetricInfoService;
	}
}
